import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { lz } from "../../i18n/localized";
import {
  INBOUND_STAGES,
  IN_STAGE_LABEL,
  OUTBOUND_STAGES,
  STAGE_LABEL,
  STAGE_SHORT,
  type InboundStage,
  type OutboundStage,
} from "../../domain/stages";
import type { StageCounts } from "../../api/types";
import styles from "./StageStrip.module.css";

export type Direction = "out" | "in";

export function stripStages(direction: Direction): readonly string[] {
  return direction === "in" ? INBOUND_STAGES : OUTBOUND_STAGES;
}

export function stageLabel(stage: string, direction: Direction): string {
  if (direction === "in") return IN_STAGE_LABEL[stage as InboundStage] ?? stage;
  return STAGE_LABEL[stage as OutboundStage] ?? (stage === "error" ? lz("domain.stageError", "Error") : stage);
}

function shortLabel(stage: string, direction: Direction): string {
  if (direction === "in") return IN_STAGE_LABEL[stage as InboundStage] ?? stage;
  return STAGE_SHORT[stage as OutboundStage] ?? stage;
}

const consoleQueue = (stage: string) => `/documents?stage=${stage}`;

/** Counters link to the queue on a dashboard; inside a queue they toggle the stage filter. No API hooks: the portal reuses it. */
export function StageStrip({
  counts,
  direction = "out",
  selected = [],
  onToggle,
  linkTo = consoleQueue,
  label,
}: {
  counts: StageCounts;
  direction?: Direction;
  selected?: string[];
  onToggle?: (stage: string) => void;
  linkTo?: (stage: string) => string;
  label?: string;
}) {
  const { t } = useTranslation();
  const stages = stripStages(direction);
  const defaultAriaLabel =
    direction === "in"
      ? t("dashboard.stageStrip.inboundStages", "Inbound stages")
      : t("dashboard.stageStrip.outboundStages", "Outbound stages");
  return (
    <nav className={styles.strip} aria-label={label ?? defaultAriaLabel}>
      {stages.map((stage) => {
        const body = (
          <>
            <span className={styles.stageName}>{shortLabel(stage, direction)}</span>
            <span className={`${styles.stageCount} num`}>{counts[stage] ?? 0}</span>
          </>
        );
        if (!onToggle) {
          return (
            <Link key={stage} className={styles.stage} to={linkTo(stage)} aria-label={stageLabel(stage, direction)}>
              {body}
            </Link>
          );
        }
        const on = selected.includes(stage);
        return (
          <button
            key={stage}
            type="button"
            className={`${styles.stage}${on ? ` ${styles.on}` : ""}`}
            aria-label={stageLabel(stage, direction)}
            aria-pressed={on}
            onClick={() => onToggle(stage)}
          >
            {body}
          </button>
        );
      })}
    </nav>
  );
}
